import {Stack, Text, useToast} from '@sanity/ui'
import {useState} from 'react'

import {useCurrentScopeContext} from '@/context/useCurrentScopeContext'
import {useRequiredOrganizationPermissions} from '@/context/useOrganizationPermissions'

import {
  type GetResourceBudgetResponse,
  useBudgets,
  useSetBudget,
} from '../../../../data/customers/useBudgets'
import {BudgetSpendingOverview} from './BudgetSpendingOverview'
import {EditBudgetDialog} from './EditBudgetDialog'

const BudgetCapSection: React.FC<{budget: GetResourceBudgetResponse}> = ({budget}) => {
  const {currentOrgId} = useCurrentScopeContext() || {}
  const canEditCap = useRequiredOrganizationPermissions(
    {
      permissions: [{grantName: 'billing', permissionName: 'sanity.organization'}],
    },
    currentOrgId
  )

  const {mutateAsync, isPending} = useSetBudget({
    organizationId: currentOrgId || '',
    groupId: budget.id,
  })

  const [showDialog, setShowDialog] = useState(false)

  const toast = useToast()

  const onCloseDialog = () => {
    setShowDialog(false)
  }

  return (
    <>
      <BudgetSpendingOverview
        currentSpend={budget.budget?.used ?? 0}
        cap={budget.budget?.maxAmount ?? null}
        defaultCap={budget.defaultMaxAmount}
        featureName={budget.name}
        canEditCap={canEditCap}
        onEditCap={() => setShowDialog(true)}
      />
      {showDialog && (
        <EditBudgetDialog
          featureName={budget.name}
          currentCustomCap={budget.budget?.maxAmount}
          currentSpend={budget.budget?.used || 0}
          defaultCap={budget.defaultMaxAmount}
          onSetCap={async (data) => {
            try {
              await mutateAsync(data)
              onCloseDialog()
              toast.push({title: 'Spending limit updated successfully', status: 'success'})
            } catch (e) {
              console.error(e)
              toast.push({title: 'Something went wrong. Please try again.', status: 'error'})
            }
          }}
          submitting={isPending}
          onCancel={onCloseDialog}
          currentCapSetting={
            budget.budget?.maxAmount === null
              ? 'unlimited'
              : budget.budget?.maxAmount === budget.defaultMaxAmount
                ? 'default'
                : 'custom'
          }
        />
      )}
    </>
  )
}

export const BudgetCapsTab: React.FC = () => {
  const {currentOrgId} = useCurrentScopeContext() || {}
  const {data} = useBudgets({organizationId: currentOrgId || ''})
  if (!data) return
  return (
    <Stack>
      {data.budgets.map((budget) => (
        <Stack space={5}>
          <Text size={3} as={'h2'} weight="bold">
            {budget.name}
          </Text>
          <BudgetCapSection budget={budget} />
        </Stack>
      ))}
    </Stack>
  )
}
